import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table, IconButton,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination, Modal,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Snackbar,
    DialogContentText,
    Alert,
    InputAdornment,
    CircularProgress,
    Tooltip
} from '@mui/material';
import { styled } from "@mui/material/styles";
import SearchIcon from '@mui/icons-material/Search';
import base64 from "base64-js";
import { Delete } from '@mui/icons-material';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

const StyledHeader = styled(Box)(({ theme }) => ({
    border: "1px solid #CC9966",
    borderRadius: "8px",
    height: "60px",
    background: "linear-gradient(90deg, #beab6c 0%, #d4c28a 100%)",
    display: "flex",
    alignItems: "center",
    padding: "0 20px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    marginBottom: "20px",
}));

const ManageVehicleType = () => {
    const navigate = useNavigate();
    const { encodedFormId } = useParams();
    const location = useLocation();
    const [vehicleTypes, setVehicleTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [authorization, setAuthorization] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [view, setView] = useState("list");
    const [editingVehicleType, setEditingVehicleType] = useState(null);
    const [filters, setFilters] = useState({ searchTerm: '' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [vehicleTypeToDelete, setVehicleTypeToDelete] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const decodeFormId = (encodedFormId) => {
        if (!encodedFormId || typeof encodedFormId !== "string") {
            console.error("encodedFormId is missing or invalid:", encodedFormId);
            return null;
        }
        try {
            const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
            const bytes = base64.toByteArray(paddedEncodedFormId);
            return new TextDecoder().decode(bytes);
        } catch (error) {
            console.error("Error decoding formId:", error);
            return null;
        }
    };

    const formId = decodeFormId(encodedFormId);

    const Authorizer = async () => {
        if (!formId) {
            console.error("formId is null, cannot call Authorizer");
            setAuthorization({ isPermission: 0 });
            return;
        }
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const token = sessionStorage.getItem("token");
            const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                },
                body: JSON.stringify({
                    formId,
                }),
            });
            if (response.ok) {
                const responseData = await response.json();
                setAuthorization(responseData[0]);
                setCanEdit(responseData[0].uModify === 1);
                setCanDelete(responseData[0].uDelete === 1);
            } else {
                console.error("Authorization failed:", response.status);
                showSnackbar("Invalid authorization response.", "error");
            }
        } catch (error) {
            console.error("API request error:", error);
            showSnackbar("An error occurred during authorization.", "error");
        }
    };

    useEffect(() => {
        if (location.state?.successMessage) {
            setSnackbarMessage(location.state.successMessage);
            setSnackbarOpen(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        if (formId) {
            fetchVehicleTypes();
            Authorizer();
        }
    }, [formId]);

    const fetchVehicleTypes = async () => {
        try {
            setIsLoading(true);
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/Vehicle/GetVehicleType`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: sessionStorage.getItem("token"),
                },
            });
            if (response.ok) {
                const responseData = await response.json();
                setVehicleTypes(responseData);
            }
        } catch (error) {
            console.error("Error fetching vehicle types:", error);
            showSnackbar("Failed to load vehicle types.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddVehicleType = () => {
        if (authorization?.uWrite !== 1) {
            showSnackbar("You don't have permission to add vehicle types.", "error");
            return;
        }
        navigate('/add-vehicle-type', {
            state: { encodedFormId }
        });
    };

    const filteredVehicleTypes = Array.isArray(vehicleTypes)
        ? vehicleTypes.filter(v => {
            const search = filters.searchTerm.toLowerCase();
            return !filters.searchTerm || (v?.vehicleType?.toLowerCase().includes(search));
        })
        : [];

    if (!authorization) {
        return <div>Loading...</div>;
    }

    if (authorization.isPermission !== 1 || !formId) {
        return <div>You are not authorized to view this page</div>;
    }

    return (
        <Container >
            <Box sx={{
                backgroundColor: "#0B1F40",
                color: "white",
                padding: "15px 20px",
                borderRadius: "6px 6px 0 0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    Vehicle Type List
                </Typography>
            </Box>
            <Paper elevation={3} sx={{ padding: 3, borderRadius: "0 0 6px 6px", marginTop: 1 }}>
                {isLoading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="200px">
                        <CircularProgress color="primary" />
                        <Typography variant="body1" mt={2}>Loading vehicle list...</Typography>
                    </Box>
                ) : (
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <TextField
                                name='searchTerm'
                                placeholder="Search"
                                style={{ width: "300px" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                value={filters.searchTerm}
                                onChange={(e) => setFilters({ ...filters, [e.target.name]: e.target.value })}
                            />

                            <Tooltip title={authorization?.uWrite === 1 ? "" : "Not authorized to add"}>
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={handleAddVehicleType}
                                        color='primary'
                                        sx={{
                                            height: 53,
                                            fontWeight: 600,
                                            paddingX: 3,
                                            paddingY: 1,
                                            // borderRadius: "6px",
                                            boxShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                                        }}
                                        disabled={authorization?.uWrite !== 1}
                                    >
                                        Add Vehicle Type
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                        <TableContainer
                            component={Paper}
                            style={{

                                marginTop: "16px",
                                border: '1px solid #ccc'
                            }}
                        >
                            <Table sx={{
                                minWidth: 650,
                                border: '1px solid #ccc',
                                '& .MuiTableCell-root': {
                                    borderRight: '1px solid #ccc',
                                    fontSize: '1.1rem',
                                },
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
                                            Vehicle Type
                                        </TableCell>
                                        <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                            Actions
                                        </TableCell>

                                    </TableRow>
                                </TableHead>
                               <TableBody>
  {isLoading ? (
    <TableRow>
      <TableCell colSpan={2} align="center">
        <CircularProgress />
      </TableCell>
    </TableRow>
  ) : filteredVehicleTypes.length > 0 ? (
    filteredVehicleTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
      <TableRow key={row.vehicleTypeId} sx={{ borderTop: '1px solid #ccc' }}>
        <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
          {row.vehicleType}
        </TableCell>
        <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
          <Tooltip title={canEdit ? "" : "Not authorized to edit"}>
            <span>
              <Button
                variant="contained"
                size="small"
                color='warning'
                onClick={() => navigate(`/manage-vehicle-type/edit/${row.vehicleTypeId}`, { state: { vehicleType: row, encodedFormId } })}
                disabled={!canEdit}
                sx={{ marginRight: 1, minWidth: 80 }}
                startIcon={<EditIcon />}
              >
                EDIT
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={canDelete ? "" : "Not authorized to delete"}>
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (!canDelete) {
                    showSnackbar("You don't have permission to delete.", "error");
                    return;
                  }
                  setVehicleTypeToDelete(row);
                  setDeleteDialogOpen(true);
                }}
                color="error"
                disabled={!canDelete}
                sx={{ minWidth: 80 }}
                startIcon={<DeleteIcon />}
              >
                DELETE
              </Button>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
    ))
  ) : (
    <TableRow>
      <TableCell colSpan={2} align="center">
        No Vehicle Types Found
      </TableCell>
    </TableRow>
  )}
</TableBody>

                            </Table>
                        </TableContainer>


                        <TablePagination
                            component="div"
                            count={filteredVehicleTypes.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            sx={{ mt: 2 }}
                        />
                    </>
                )}
            </Paper>
            <Modal
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setVehicleTypeToDelete(null);
                }}
                aria-labelledby="delete-modal-title"
                aria-describedby="delete-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
                        <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
                            Delete Confirmation ⚠️
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: '20px' }}>
                        <Typography id="delete-modal-description" sx={{ mb: 2 }}>
                            Are you sure you want to delete this Vehicle Type ?
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setDeleteDialogOpen(false);
                                    setVehicleTypeToDelete(null);
                                }}
                                variant="outlined"
                                sx={{ mr: 1 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={async () => {
                                    if (!vehicleTypeToDelete) return;
                                    try {
                                        const response = await fetch(
                                            `${process.env.REACT_APP_BASE_URL}/Transportation/VehicleType/${vehicleTypeToDelete.vehicleTypeId}`,
                                            {
                                                method: "DELETE",
                                                headers: {
                                                    Authorization: sessionStorage.getItem("token"),
                                                },
                                            }
                                        );
                                        if (response.ok) {
                                            setVehicleTypes((prev) =>
                                                prev.filter(
                                                    (v) =>
                                                        v.vehicleTypeId !== vehicleTypeToDelete.vehicleTypeId
                                                )
                                            );
                                            showSnackbar("Vehicle type deleted successfully!", "success");
                                        } else {
                                            showSnackbar("Failed to delete vehicle type.", "error");
                                        }
                                    } catch (error) {
                                        showSnackbar("Error deleting vehicle type.", "error");
                                    } finally {
                                        setDeleteDialogOpen(false);
                                        setVehicleTypeToDelete(null);
                                    }
                                }}
                            >
                                Yes, Delete
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{
                    width: '100%',
                    ...(snackbarSeverity === 'success' && {
                        backgroundColor: '#2e7d32',
                        color: '#fff',
                    }),
                }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ManageVehicleType;
